/* =====================================================================
 * Workspace Sync — chatStore ↔ Backend API 동기화 레이어
 *
 * Conversation (프론트엔드) ↔ WorkspaceItem (백엔드) 간 변환 및
 * 비동기 동기화를 담당합니다.
 * ===================================================================== */

import type {
  Conversation,
  WorkspaceItem,
  WorkspaceScope,
  WorkspaceKind,
  WorkspaceMode,
  ChatMessage,
  AgentCompletedData,
  WidgetData,
  Reaction,
  FileAttachment,
  WorkspaceMessage,
} from '@auw/workspace-utils';
import * as wsApi from './workspace';
import { hasAuthSessionContext } from './authFetch';
import { isUnauthorizedHttpError } from '@auw/workspace-utils';
import { normalizeConversationLiveView } from '@auw/workspace-utils';
import { buildConversationSettings } from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* 변환 함수                                                            */
/* ------------------------------------------------------------------ */

function normalizeProfilePicture(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
  if (trimmed.startsWith('data:image/')) return trimmed;
  if (trimmed.startsWith('/')) return trimmed;

  try {
    const url = new URL(trimmed);
    if (['http:', 'https:', 'blob:'].includes(url.protocol)) return trimmed;
  } catch {
    return null;
  }
  return null;
}

function normalizeSharedGroup(raw: unknown): Conversation['sharedGroup'] {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw as Record<string, unknown>;

  const legacyConversationId = typeof src.conversation_id === 'string' ? src.conversation_id : '';
  const groupIdRaw = typeof src.group_id === 'string' ? src.group_id : legacyConversationId;
  if (!groupIdRaw) return null;

  const teamId = typeof src.team_id === 'string' ? src.team_id : '';
  if (!teamId) return null;

  const membersRaw = Array.isArray(src.members) ? src.members : [];
  const members = membersRaw
    .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object' && typeof (m as Record<string, unknown>).user_id === 'string')
    .map((m) => ({
      user_id: m.user_id as string,
      name: typeof m.name === 'string' ? m.name : null,
      email: typeof m.email === 'string' ? m.email : null,
      picture: normalizeProfilePicture(m.picture),
    }));

  const memberIdsRaw = Array.isArray(src.member_ids) ? src.member_ids : [];
  const memberIds = memberIdsRaw
    .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  const normalizedMemberIds = memberIds.length > 0 ? memberIds : members.map((m) => m.user_id);

  return {
    group_id: groupIdRaw,
    team_id: teamId,
    team_name: typeof src.team_name === 'string' ? src.team_name : null,
    title: typeof src.title === 'string' && src.title.trim().length > 0 ? src.title : '그룹 협업',
    state: src.state === 'team_deleted' ? 'team_deleted' : 'active',
    member_ids: normalizedMemberIds,
    members,
    created_at: typeof src.created_at === 'string' ? src.created_at : new Date().toISOString(),
  };
}

type AttachmentMeta = {
  file_id: string;
  name?: string;
  mime_type?: string;
  size?: number;
};

function buildAttachmentFromMeta(meta: AttachmentMeta): FileAttachment | null {
  if (!meta.file_id || typeof meta.file_id !== 'string') return null;
  const name = (typeof meta.name === 'string' && meta.name.trim()) || meta.file_id;
  const type = typeof meta.mime_type === 'string' ? meta.mime_type : 'application/octet-stream';
  const size = typeof meta.size === 'number' ? meta.size : 0;
  const serveUrl = wsApi.getFileServeUrl(meta.file_id);
  const fileObj =
    typeof File !== 'undefined'
      ? new File([], name, { type })
      : ({ name, size: 0, type } as unknown as File);
  return {
    id: meta.file_id,
    name,
    type,
    size,
    previewUrl: undefined,
    file: fileObj,
    remoteUrl: serveUrl,
  };
}

function parseMessageAttachments(msg: WorkspaceMessage): FileAttachment[] | undefined {
  const metadata = (msg.metadata ?? {}) as Record<string, unknown>;
  const raw = metadata.attachment_files;
  if (Array.isArray(raw)) {
    const parsed = raw
      .filter((v): v is AttachmentMeta => !!v && typeof v === 'object' && typeof (v as AttachmentMeta).file_id === 'string')
      .map(buildAttachmentFromMeta)
      .filter((v): v is FileAttachment => !!v);
    if (parsed.length > 0) return parsed;
  }

  if (Array.isArray(msg.attachments) && msg.attachments.length > 0) {
    const fallback = msg.attachments
      .filter((fid): fid is string => typeof fid === 'string' && fid.trim().length > 0)
      .map((fid) => buildAttachmentFromMeta({ file_id: fid }))
      .filter((v): v is FileAttachment => !!v);
    if (fallback.length > 0) return fallback;
  }
  return undefined;
}

function extractAttachmentMeta(
  message: ChatMessage,
): { attachmentFileIds: string[]; attachmentFilesMeta: AttachmentMeta[] } {
  const out: AttachmentMeta[] = [];
  for (const att of message.attachments ?? []) {
    const isRemote = !!att.remoteUrl || (att.file?.size ?? 0) === 0;
    if (!isRemote) continue;
    const fileId = att.id;
    if (!fileId || typeof fileId !== 'string') continue;
    out.push({
      file_id: fileId,
      name: att.name,
      mime_type: att.type,
      size: att.size,
    });
  }
  return {
    attachmentFileIds: out.map((a) => a.file_id),
    attachmentFilesMeta: out,
  };
}

/** WorkspaceItem (백엔드) → Conversation (프론트엔드) */
export function itemToConversation(item: WorkspaceItem): Conversation {
  const sharedGroupRaw = normalizeSharedGroup(item.settings?.sharedGroup);
  return {
    id: item.item_id,
    title: item.title,
    mode: item.mode as WorkspaceMode,
    workspaceScope: item.scope as WorkspaceScope,
    workspaceKind: item.kind as WorkspaceKind,
    workspaceTeamId: item.team_id,
    workspaceParentId: item.parent_id,
    startPagePending: (item.settings?.startPagePending as boolean) ?? false,
    startPageState: (item.settings?.startPageState as Conversation['startPageState']) ?? {},
    messages: [],
    historyLoaded: false,
    sessionId: item.session_id ?? undefined,
    pinnedAt: (item.settings?.pinnedAt as string) ?? null,
    sharedGroup: sharedGroupRaw,
    liveView: normalizeConversationLiveView(item.settings?.liveView),
    workspaceSettings: item.settings ?? {},
    createdAt: new Date(item.created_at).getTime(),
    updatedAt: new Date(item.updated_at).getTime(),
  };
}

/** Conversation (프론트엔드) → WorkspaceItem (StoragePage용) */
export function conversationToItem(conv: Conversation): WorkspaceItem {
  return {
    item_id: conv.id,
    user_id: '',
    scope: conv.workspaceScope,
    team_id: conv.workspaceTeamId,
    mode: conv.mode,
    kind: conv.workspaceKind,
    title: conv.title,
    parent_id: conv.workspaceParentId,
    sort_order: 0,
    session_id: conv.sessionId ?? null,
    settings: buildConversationSettings(conv),
    status: 'active',
    created_at: new Date(conv.createdAt).toISOString(),
    updated_at: new Date(conv.updatedAt).toISOString(),
  };
}

/** Conversation (프론트엔드) → CreateItemParams (백엔드) */
export function conversationToCreateParams(conv: Conversation): wsApi.CreateItemParams {
  return {
    item_id: conv.id,
    mode: conv.mode,
    kind: conv.workspaceKind,
    title: conv.title,
    scope: conv.workspaceScope,
    team_id: conv.workspaceTeamId,
    parent_id: conv.workspaceParentId,
    sort_order: 0,
    session_id: conv.sessionId ?? null,
    settings: buildConversationSettings(conv),
  };
}

/* ------------------------------------------------------------------ */
/* 동기화 함수 — fire-and-forget (비차단)                                */
/* ------------------------------------------------------------------ */

/** 아이템 생성 동기화. 백엔드 item_id를 반환합니다. */
export async function syncCreateItem(conv: Conversation): Promise<string | null> {
  try {
    const item = await wsApi.createItem(conversationToCreateParams(conv));
    return item.item_id;
  } catch (err) {
    console.error('[WorkspaceSync] 아이템 생성 실패:', err);
    return null;
  }
}

/** 아이템 업데이트 동기화 */
export async function syncUpdateItem(
  itemId: string,
  updates: wsApi.UpdateItemParams,
): Promise<boolean> {
  try {
    await wsApi.updateItem(itemId, updates);
    return true;
  } catch (err) {
    console.error('[WorkspaceSync] 아이템 업데이트 실패:', err);
    return false;
  }
}

/** 아이템 삭제 동기화 */
export async function syncDeleteItem(itemId: string): Promise<boolean> {
  try {
    await wsApi.deleteItem(itemId);
    return true;
  } catch (err) {
    console.error('[WorkspaceSync] 아이템 삭제 실패:', err);
    return false;
  }
}

/** 아이템 이동 동기화 */
export async function syncMoveItem(
  itemId: string,
  params: wsApi.MoveItemParams,
): Promise<boolean> {
  try {
    await wsApi.moveItem(itemId, params);
    return true;
  } catch (err) {
    console.error('[WorkspaceSync] 아이템 이동 실패:', err);
    return false;
  }
}

/** 아이템 제목 변경 동기화 */
export async function syncRenameItem(
  itemId: string,
  title: string,
): Promise<boolean> {
  return syncUpdateItem(itemId, { title });
}

/* ------------------------------------------------------------------ */
/* 전체 로드 — 백엔드에서 전체 아이템 목록 가져오기                          */
/* ------------------------------------------------------------------ */

export interface LoadResult {
  conversations: Record<string, Conversation>;
  order: string[];
  workspaceOrder: string[];
  count: number;
  loaded: number;
  hasMore: boolean;
}

interface LoadFromBackendOptions {
  limit?: number;
  skip?: number;
  fetchAll?: boolean;
  mode?: WorkspaceMode;
}

const INITIAL_ITEMS_LIMIT = 120;
const PAGE_ITEMS_LIMIT = 200;
const MESSAGE_LOAD_LIMIT = 200;

function buildLoadResultFromItems(items: WorkspaceItem[], count: number): LoadResult {
  if (!items || items.length === 0) {
    return { conversations: {}, order: [], workspaceOrder: [], count, loaded: 0, hasMore: false };
  }

  const conversations: Record<string, Conversation> = {};
  for (const item of items) {
    const conv = itemToConversation(item);
    conversations[conv.id] = conv;
  }

  const getPinnedTs = (item: WorkspaceItem): number => {
    const raw = item.settings?.pinnedAt;
    if (typeof raw !== 'string' || raw.trim().length === 0) return 0;
    const ts = new Date(raw).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  };

  const sorted = items.sort((a, b) => {
    const pinnedDiff = getPinnedTs(b) - getPinnedTs(a);
    if (pinnedDiff !== 0) return pinnedDiff;
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });
  const order = sorted
    .filter((item) => item.kind === 'page')
    .map((item) => item.item_id);
  const workspaceOrder = sorted.map((item) => item.item_id);

  return {
    conversations,
    order,
    workspaceOrder,
    count,
    loaded: items.length,
    hasMore: items.length < count,
  };
}

/** 백엔드에서 워크스페이스 아이템을 로드합니다. */
export async function loadFromBackend(
  options: LoadFromBackendOptions = {},
): Promise<LoadResult | null> {
  try {
    const fetchAll = options.fetchAll ?? false;
    const pageLimit = options.limit ?? (fetchAll ? PAGE_ITEMS_LIMIT : INITIAL_ITEMS_LIMIT);

    const items: WorkspaceItem[] = [];
    let count = 0;
    let skip = options.skip ?? 0;

    while (true) {
      const resp = await wsApi.listItems({ mode: options.mode, limit: pageLimit, skip });
      if (count === 0) count = resp.count;
      if (!resp.items || resp.items.length === 0) break;
      items.push(...resp.items);
      skip += resp.items.length;
      if (!fetchAll) break;
      if (skip >= count) break;
    }

    return buildLoadResultFromItems(items, count);
  } catch (err) {
    if (isUnauthorizedHttpError(err) && !hasAuthSessionContext()) {
      return null;
    }
    console.error('[WorkspaceSync] 백엔드 로드 실패:', err);
    return null;
  }
}

/** 로그인 직후 빠른 초기 렌더를 위한 recent 아이템 로드 */
export async function loadRecentFromBackend(limit = 12, mode?: string): Promise<LoadResult | null> {
  try {
    const safeLimit = Math.max(1, Math.min(50, Math.floor(limit)));
    const resp = await wsApi.getRecentItems(mode, safeLimit);
    const items = resp.items ?? [];
    const count = typeof resp.count === 'number' ? resp.count : items.length;
    return buildLoadResultFromItems(items, count);
  } catch (err) {
    console.error('[WorkspaceSync] recent 아이템 로드 실패:', err);
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* 메시지 동기화                                                        */
/* ------------------------------------------------------------------ */

/** 아이템의 메시지 목록을 백엔드에서 로드합니다. */
export async function loadMessages(itemId: string): Promise<ChatMessage[]> {
  try {
    const { messages } = await wsApi.listMessages(itemId, { limit: MESSAGE_LOAD_LIMIT });
    return messages.map((msg) => {
      const completedData = (msg.metadata?.completedData as AgentCompletedData & { widgets?: unknown[] }) ?? undefined;

      let widgets: WidgetData[] | undefined;
      if (completedData && Array.isArray(completedData.widgets) && completedData.widgets.length > 0) {
        widgets = completedData.widgets
          .filter((w): w is Record<string, unknown> => w != null && typeof w === 'object')
          .map((w) => ({
            widgetId: (w.widget_id as string) || `w_${Date.now()}`,
            title: (w.title as string) || 'widget',
            widgetCode: (w.widget_code as string) || '',
            width: (w.width as number) || 800,
            height: (w.height as number) || 600,
            isSvg: (w.is_svg as boolean) || false,
            isStreaming: false,
          }));
      }

      return {
        id: msg.message_id,
        role: msg.role as ChatMessage['role'],
        content: msg.content,
        contentType: msg.content_type || 'text',
        attachments: parseMessageAttachments(msg),
        createdAt: new Date(msg.created_at).getTime(),
        isStreaming: false,
        streamEvents: [],
        completedData,
        widgets,
        senderId: msg.user_id || (msg.metadata?.senderId as string) || null,
        senderName: (msg.metadata?.senderName as string) || null,
        reactions: (msg.metadata?.reactions as Reaction[]) || undefined,
      };
    });
  } catch (err) {
    console.error('[WorkspaceSync] 메시지 로드 실패:', err);
    return [];
  }
}

/** 메시지를 백엔드에 저장합니다. */
export async function syncSaveMessage(
  itemId: string,
  message: ChatMessage,
): Promise<WorkspaceMessage | null> {
  try {
    const metadata: Record<string, unknown> = {
      client_message_id: message.id,
    };
    if (message.completedData) {
      metadata.completedData = message.completedData;
    }
    if (message.senderId) {
      metadata.senderId = message.senderId;
    }
    if (message.senderName) {
      metadata.senderName = message.senderName;
    }
    // 그룹채팅 @멘션 대상 — 백엔드가 DM 멘션 알림을 발송하는 데 사용
    const mentionedUserIds = message.metadata?.mentioned_user_ids;
    if (Array.isArray(mentionedUserIds) && mentionedUserIds.length > 0) {
      metadata.mentioned_user_ids = mentionedUserIds;
    }
    const { attachmentFileIds, attachmentFilesMeta } = extractAttachmentMeta(message);
    if (attachmentFilesMeta.length > 0) {
      metadata.attachment_files = attachmentFilesMeta;
    }
    const saved = await wsApi.createMessage(itemId, {
      role: message.role,
      content: message.content,
      content_type: message.contentType || 'text',
      attachments: attachmentFileIds.length > 0 ? attachmentFileIds : undefined,
      metadata,
    });
    return saved;
  } catch (err) {
    console.error('[WorkspaceSync] 메시지 저장 실패:', err);
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* 콘텐츠 동기화                                                        */
/* ------------------------------------------------------------------ */

/** 페이지 콘텐츠를 백엔드에 저장합니다. */
export async function syncSaveContent(
  itemId: string,
  contentType: string,
  content: unknown,
  expectedVersion?: number,
): Promise<number | null> {
  try {
    const result = await wsApi.saveContent(itemId, {
      content_type: contentType,
      content,
      expected_version: expectedVersion,
    });
    return result.version;
  } catch (err) {
    console.error('[WorkspaceSync] 콘텐츠 저장 실패:', err);
    return null;
  }
}

/** 페이지 콘텐츠를 백엔드에서 로드합니다. */
export async function loadContent(itemId: string) {
  try {
    return await wsApi.getContent(itemId);
  } catch (err) {
    console.error('[WorkspaceSync] 콘텐츠 로드 실패:', err);
    return null;
  }
}
